import { motion } from 'framer-motion';
import { Award, Heart, Target, Users } from 'lucide-react';
import { brandInfo } from '../data/products';

export const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Premium Craftsmanship',
      description: 'Every piece is meticulously crafted by skilled artisans using only the finest materials sourced from around the world.'
    },
    {
      icon: Heart,
      title: 'Timeless Design',
      description: 'We create furniture that transcends trends, bringing lasting elegance and sophistication to your living spaces.'
    },
    {
      icon: Target,
      title: 'Attention to Detail',
      description: 'From the grain of the wood to the stitching of the fabric, no detail is too small in our pursuit of perfection.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We are committed to providing exceptional service at every step of your journey.'
    }
  ];

  return (
    <main className="pt-24 pb-24 bg-[#FAF8F5]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] sm:h-[60vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=2669"
            alt="Luxury Furniture Showroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#2D4A3E]/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              Our Story
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-white mt-4 leading-none">
              About Bright Vista
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
                Our Mission
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3 mb-6">
                Bringing Timeless Elegance to Every Home
              </h2>
              <div className="font-['Montserrat'] text-[#5A6B5D] leading-relaxed space-y-4 text-sm sm:text-base">
                <p>
                  At Bright Vista, we believe that furniture is more than just functional pieces—it's an expression of your lifestyle, a reflection of your taste, and the foundation of your home's character.
                </p>
                <p>
                  Founded with a passion for exceptional design and uncompromising quality, we curate a collection of luxury furniture that combines modern aesthetics with timeless craftsmanship.
                </p>
                <p>
                  From the initial sketch to the final polish, our master craftsmen pour their expertise and dedication into every detail. We source the finest materials to create furniture that will be cherished for generations.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=800"
                alt="Luxury Furniture"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#C67D4E] p-6 max-w-[160px] hidden md:block">
                <p className="font-['Cormorant_Garamond'] text-3xl text-white font-medium">10+</p>
                <p className="font-['Montserrat'] text-white/80 text-xs uppercase tracking-wider mt-1">
                  Years of Excellence
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              What We Stand For
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#FAF8F5] p-6 md:p-8 border border-[#E8E4DE]"
              >
                <div className="w-12 h-12 bg-[#C67D4E]/10 rounded-full flex items-center justify-center mb-4">
                  <value.icon className="text-[#C67D4E]" size={22} />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2D4A3E] mb-3">
                  {value.title}
                </h3>
                <p className="font-['Montserrat'] text-[#5A6B5D] text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-16 md:py-24 bg-[#2D4A3E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              Our Promise
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-white mt-3 mb-6">
              Committed to Your Satisfaction
            </h2>
            <p className="font-['Montserrat'] text-[#A8B5AB] leading-relaxed mb-10 text-sm sm:text-base">
              We stand behind every piece we sell. From our 30-day easy return policy to our dedicated customer support team, we're here to ensure your complete satisfaction.
            </p>
            <div className="grid grid-cols-3 gap-4 md:gap-8">
              <div>
                <p className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#C67D4E]">5,000+</p>
                <p className="font-['Montserrat'] text-[10px] md:text-xs text-[#A8B5AB] uppercase tracking-wider mt-2">Happy Customers</p>
              </div>
              <div>
                <p className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#C67D4E]">500+</p>
                <p className="font-['Montserrat'] text-[10px] md:text-xs text-[#A8B5AB] uppercase tracking-wider mt-2">Unique Designs</p>
              </div>
              <div>
                <p className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#C67D4E]">50</p>
                <p className="font-['Montserrat'] text-[10px] md:text-xs text-[#A8B5AB] uppercase tracking-wider mt-2">States Served</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
                Visit Us
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3 mb-6">
                Our Showroom
              </h2>
              <p className="font-['Montserrat'] text-[#5A6B5D] leading-relaxed mb-8 text-sm sm:text-base">
                Experience the beauty and craftsmanship of Bright Vista furniture in person. Visit our flagship showroom in New York City.
              </p>
              <div className="font-['Montserrat'] text-[#2D4A3E] space-y-2 text-sm">
                <p className="font-semibold">{brandInfo.name}</p>
                <p>{brandInfo.address}</p>
                <p>{brandInfo.phone}</p>
                <p>{brandInfo.email}</p>
                <p className="text-[#8B7355] mt-4">{brandInfo.hours}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#E8E4DE] aspect-video"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976397304603!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1704067200000!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Bright Vista Location"
                data-testid="map-embed"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};
